// src/config/landing.js

/**
 * Where a user should land after login, derived from their ASSET SCOPE.
 *
 * Scope is stored as three levels — factories / ous / machines — and an EMPTY level
 * means "no filter, all of them" (David 2026-08-09; the rule and its anti-widening
 * guard live in user-management/src/constants/roles.js). The corollary is what matters
 * here: a level that names exactly one asset describes a user who only ever sees that
 * asset, so dropping them on the company map makes them click down through a hierarchy
 * they do not have. ROTHENBERGER reported precisely that — one factory, one machine,
 * still landing on the global map (monday 12943481904).
 *
 * Walk narrowest → widest and take the first level that names exactly one asset:
 *
 *   machines: ['M1']    → /app/machine/M1
 *   factories: ['F1']   → /app/dashboard/factory/F1   (that factory's OUs)
 *   otherwise           → null; the caller keeps its company-level default
 *
 * A level naming two or more assets is a real restriction but has no single page to
 * open, so it falls through to the next level out.
 *
 * The OU level is deliberately absent. There is no OU page in the Dashboard MFE —
 * its parseUrl only accepts `company` and `factory`, and `/app/dashboard/ou/<id>`
 * falls back to the companies list. The only OU-level surface is PublicView, which is
 * feature-gated and is a wall-display view rather than a personal landing page. A user
 * scoped to one OU therefore falls through to the next level OUT: to their factory when
 * that level names one, and otherwise all the way to the company dashboard.
 */

import { ROUTES } from './routes';

/** Permission levels may hold bare ids or {id} objects; both appear in the wild. */
const idOf = (entry) => (entry && typeof entry === 'object' ? entry.id : entry);

/** The single id at a level, or null when the level is empty ("all") or names several. */
const onlyId = (level) => {
    const ids = (level || []).map(idOf).filter(Boolean);
    return ids.length === 1 ? String(ids[0]) : null;
};

/**
 * @param {object} auth
 * @param {object} [auth.permissions]      - { company, factories, ous, machines }
 * @param {boolean} [auth.isSuperuser]     - superusers pick a company; they have no scope
 * @param {boolean} [auth.hasAssignedCompany]
 * @returns {{level: 'machine'|'factory', id: string, path: string}|null}
 */
export const resolveLandingScope = ({ permissions, isSuperuser, hasAssignedCompany } = {}) => {
    // A superuser's empty scope means "everything", and a user with no company has
    // nothing to be scoped within. Both keep the existing company-selection landing.
    if (isSuperuser || !hasAssignedCompany || !permissions) return null;

    const machineId = onlyId(permissions.machines);
    if (machineId) {
        return { level: 'machine', id: machineId, path: ROUTES.machine(machineId) };
    }

    const factoryId = onlyId(permissions.factories);
    if (factoryId) {
        // ROUTES.ous(factoryId) is the factory page — it lists that factory's OUs.
        return { level: 'factory', id: factoryId, path: ROUTES.ous(factoryId) };
    }

    return null;
};
